/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    TemplateRuleDto,
    TemplateRulesListDto,
    TemplateRulesListSearchParams,
} from '../api/alerts/alertsClient.typings';
import { AnyAction } from 'redux';
import { PageQueryParams } from '../api/apiCommon.typings';
import { AlertTemplateRulesAction, AlertTemplateRulesActionType } from '../actions/AAlertTemplateRules';

export const rowsPerPage = [10, 20, 30, 40];
export const defaultPaginationLimit = rowsPerPage[0];

export interface AlertTemplateRulesState {
    pageParams: PageQueryParams;
    filterParams: TemplateRulesListSearchParams;
    rules: TemplateRulesListDto;
    selectedRules: TemplateRuleDto[];
    selectedIdentifier: string;
    identifiersList: string[];
    isLoadingIdentifiers: boolean;
    isLoading: boolean;
    isDownloadingRulesList: boolean;
}

const initialState: AlertTemplateRulesState = {
    rules: null,
    selectedIdentifier: null,
    selectedRules: [],
    isLoadingIdentifiers: false,
    isLoading: false,
    pageParams: {
        limit: defaultPaginationLimit,
        offset: 0,
    },
    filterParams: {
        taxNumber: '',
        ruleType: null,
        ruleEntityType: null,
    },
    identifiersList: [],
    isDownloadingRulesList: false,
};

export function alertTemplateRulesReducer(
    state: AlertTemplateRulesState = initialState,
    action: AlertTemplateRulesAction & AnyAction,
): AlertTemplateRulesState {
    switch (action.type) {
        case AlertTemplateRulesActionType.OPEN_ALERT_TEMPLATE_RULES_PAGE:
            return {
                ...state,
                isLoading: true,
            };

        case AlertTemplateRulesActionType.APPLY_PAGING:
            return {
                ...state,
                pageParams: action.payload,
                isLoading: true,
            };

        case AlertTemplateRulesActionType.APPLY_FILTER:
            return {
                ...state,
                isLoading: true,
                pageParams: { ...state.pageParams, offset: 0 },
                filterParams: action.payload,
            };

        case AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULES_SUCCESS:
            return {
                ...state,
                rules: action.payload,
                isLoading: false,
            };

        case AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_START:
            return {
                ...state,
                isLoadingIdentifiers: true,
            };
        case AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_SUCCESS:
            return {
                ...state,
                identifiersList: action.payload,
                isLoadingIdentifiers: false,
            };
        case AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_ERROR:
            return {
                ...state,
                isLoadingIdentifiers: false,
            };
        case AlertTemplateRulesActionType.CLOSE_ALERT_TEMPLATE_RULE_INFO_MODAL:
            return {
                ...state,
                selectedIdentifier: null,
                identifiersList: [],
                isLoadingIdentifiers: false,
            };

        case AlertTemplateRulesActionType.ALERTS_ALERT_TEMPLATE_RULES_ERROR:
            return {
                ...state,
                rules: null,
                isLoading: false,
            };

        case AlertTemplateRulesActionType.SELECT_ALERT_TEMPLATE_RULES:
            return {
                ...state,
                selectedRules: action.payload,
            };

        case AlertTemplateRulesActionType.CREATE_ALERT_TEMPLATE_RULES_START:
        case AlertTemplateRulesActionType.EDIT_ALERT_TEMPLATE_RULE_START:
        case AlertTemplateRulesActionType.DELETE_ALERT_TEMPLATE_RULES_START:
            return {
                ...state,
                isLoading: true,
            };

        case AlertTemplateRulesActionType.CREATE_ALERT_TEMPLATE_RULES_SUCCESS:
        case AlertTemplateRulesActionType.EDIT_ALERT_TEMPLATE_RULE_SUCCESS:
        case AlertTemplateRulesActionType.DELETE_ALERT_TEMPLATE_RULES_SUCCESS:
            return {
                ...state,
                isLoading: false,
            };

        case AlertTemplateRulesActionType.CREATE_ALERT_TEMPLATE_RULES_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case AlertTemplateRulesActionType.DOWNLOAD_TEMPLATE_RULES_START:
            return {
                ...state,
                isDownloadingRulesList: true,
            };

        case AlertTemplateRulesActionType.DOWNLOAD_TEMPLATE_RULES_SUCCESS:
        case AlertTemplateRulesActionType.DOWNLOAD_TEMPLATE_RULES_ERROR:
            return {
                ...state,
                isDownloadingRulesList: false,
            };

        case AlertTemplateRulesActionType.EXIT_ALERT_TEMPLATE_RULES_PAGE:
            return initialState;

        default:
            return state;
    }
}

export default alertTemplateRulesReducer;
